import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";
import useInstallPrompt from "../../hooks/useInstallPrompt";
import { isIos } from "../../utils/pwa";

const DISMISSED_KEY = "rmf_install_prompt_dismissed";

// Custom install UI: browsers stopped showing their own install banner years
// ago once a page calls preventDefault() on beforeinstallprompt, so this is
// the only way users see an install option here — shown right away, on
// first load. Dismissing it is permanent for this banner (so it doesn't
// nag every visit), but the same install action always stays reachable
// from Settings via InstallCard, so dismissing here never forecloses it.
export default function InstallPrompt() {
  const { standalone, canPromptNatively, install } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === "true"
  );
  // Merchant sign-up (and the phone upload page its QR code opens) is for
  // restaurant owners, not customers, so no install pitch there.
  const { pathname } = useLocation();

  if (dismissed || standalone || pathname.startsWith("/partner-signup")) return null;

  const dismissForever = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  };

  const handleInstallClick = async () => {
    const outcome = await install();
    if (outcome === "accepted" || outcome === "dismissed") setDismissed(true);
  };

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        left: { xs: 12, sm: 24 },
        right: { xs: 12, sm: "auto" },
        bottom: { xs: 12, sm: 24 },
        maxWidth: 380,
        p: 2,
        borderRadius: 3,
        zIndex: (t) => t.zIndex.modal + 100,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Install RushmyFood
        </Typography>
        {isIos() && !canPromptNatively ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Tap the Share button <IosShareIcon sx={{ fontSize: 14, verticalAlign: "middle" }} />, then
            "Add to Home Screen".
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Add the app to your home screen for a faster checkout and order updates.
          </Typography>
        )}
        {!isIos() && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleInstallClick}
            sx={{ mt: 1.5, textTransform: "none" }}
          >
            Install App
          </Button>
        )}
      </Box>
      <IconButton size="small" aria-label="Dismiss install prompt" onClick={dismissForever}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}
